import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Center,
  HStack,
  Spinner,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import CodeMirror, { EditorView } from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";
import React from "react";
import { vscodeDark } from "@uiw/codemirror-theme-vscode";
import { EditorState } from "@codemirror/state";
import { IHightlighType } from "./Quiz/types";
import EditorDots from "../components/EditorDots";
import EditorControls from "../components/EditorControls";
import {
  createHighlightPlugin,
  LineHighlight,
} from "../../utils/editorPlugins";
import EditorExplanation from "../components/EditorExplanation";
import { useCodeExecution } from "../../core/Module/api";
import { useRive, useStateMachineInput } from "@rive-app/react-canvas";

export type HighlightNumberType = {
  component: IConfig["component"];
  props: IConfig["props"];
  highlightLines: number[];
  lineColor?: IHightlighType;
  markStyle?: IHightlighType;
};

export type CodeEditorProps = {
  code: string;
  onOutput?: (ouput: string) => void;
  onRun?: ({
    code,
  }: {
    currentExplanationIndex: number;
    code: string;
    setCode: any;
    setOutput: any;
    runCode: any;
  }) => void;
  onChange?: (code: string) => void;
  hightlight?: IHightlighType;
  mode: "try" | "view" | "reveal" | "reveal-try" | "view-run";
  crowded?: boolean;
  noReset?: boolean;
  explanation?: HighlightNumberType[];
  autoHighlightChange?: boolean;
  height?: string;
  showExplanation?: boolean;
  noDots?: boolean;
  noLineNumbers?: boolean;
  language: string;
  riveSrc?: string;
  riveAnimateInitialState?: number;
  riveAnimationHeight?: string;
  riveAnimationWidth?: string;
  riveAnimationPosition?: "above-editor" | "below-editor" | "below-explanation";
  componentLevel?: boolean;
  noScroll?: boolean;
};

const STATE_MACHINE_NAME = "State Machine 1";

function CodeEditor(props: CodeEditorProps) {
  const {
    code: initial_code,
    hightlight = "neutral",
    mode = "try",
    crowded,
    noReset,
    explanation,
    showExplanation = false,
    noDots = false,
    noLineNumbers = false,
    height = "2rem",
    language,
    riveSrc,
    riveAnimationPosition = "above-editor",
    riveAnimationWidth,
    riveAnimationHeight,
    riveAnimateInitialState,
    noScroll,
  } = props;

  const [code, setCode] = useState(() => initial_code);
  const [output, setOutput] = useState("");
  const [currentHighlightIndex, setCurrentHighlightIndex] = useState(0);
  const [hightlightColor, setHightlightColor] = useState(() => hightlight);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const codeExecute = useCodeExecution();
  const [viewRef, setViewRef] = useState<EditorView | null>(null);
  const editorWrapperRef = useRef<HTMLDivElement | null>(null);

  const { rive, RiveComponent } = useRive({
    src: riveSrc,
    stateMachines: STATE_MACHINE_NAME,
    autoplay: true,
  });

  const isPlayingInput = useStateMachineInput(
    rive,
    STATE_MACHINE_NAME,
    "step",
    riveAnimateInitialState ?? 0
  );

  const hasHighlights = explanation && explanation.length > 0;
  const currentExplanation = hasHighlights
    ? explanation[currentHighlightIndex]
    : null;

  const getcurrentExplanation = useCallback((): LineHighlight | null => {
    if (!currentExplanation?.highlightLines) return null;

    return {
      lines: currentExplanation.highlightLines,
      lineStyle: currentExplanation.lineColor
        ? currentExplanation.lineColor
        : hightlightColor,
      markStyle: currentExplanation.markStyle
        ? currentExplanation.markStyle
        : hightlightColor,
    };
  }, [currentExplanation, hightlightColor]);

  const highlightPlugin = createHighlightPlugin(getcurrentExplanation);

  useEffect(() => {
    setHightlightColor(hightlight);
  }, [hightlight]);

  useEffect(() => {
    setCode(initial_code);
  }, [initial_code]);

  const onChange = React.useCallback((val: any) => {
    if (props.onChange) {
      props.onChange(val);
    }
    setCode(val);
  }, []);

  const runCode = useCallback(() => {
    {
      codeExecute.mutate(
        { language, code },
        {
          onSuccess: (res) => {
            setOutput(res.run.stderr || res.run.stdout);
          },
        }
      );
    }
  }, [code, language]);

  const handlePrevious = () => {
    if (currentHighlightIndex > 0) {
      setCurrentHighlightIndex(currentHighlightIndex - 1);
    }
  };

  const handleNext = () => {
    if (explanation && currentHighlightIndex < explanation.length - 1) {
      setCurrentHighlightIndex(currentHighlightIndex + 1);
    }
  };

  useEffect(() => {
    if (isPlayingInput) {
      isPlayingInput.value =
        (riveAnimateInitialState ?? 0) + currentHighlightIndex;
    }
  }, [
    currentHighlightIndex,
    riveAnimateInitialState,
    riveSrc,
    rive,
    isPlayingInput,
  ]);

  useEffect(() => {
    if (mode === "reveal" || mode === "reveal-try") {
      runCode();
    }
  }, [mode]);

  const ensureLineCentered = (lineToScroll: number) => {
    if (!viewRef || !editorWrapperRef.current) return;

    const line = viewRef.state.doc.line(lineToScroll);
    const linePos = line.from;

    // Get wrapper dimensions
    const wrapper = editorWrapperRef.current;
    const { top: wrapperTop, bottom: wrapperBottom } =
      wrapper.getBoundingClientRect();

    // Get coordinates of the wrapped line parts
    let lineTop = Infinity;
    let lineBottom = -Infinity;

    // Loop through positions where the line might wrap to get the full height
    for (let i = linePos; i < linePos + line.length; i++) {
      const lineCoords = viewRef.coordsAtPos(i);
      if (lineCoords) {
        // Update lineTop and lineBottom to cover the entire wrapped line
        lineTop = Math.min(lineTop, lineCoords.top);
        lineBottom = Math.max(lineBottom, lineCoords.bottom);
      }
    }

    // Check if the entire wrapped line is visible
    const isOutOfView = lineTop < wrapperTop || lineBottom > wrapperBottom;

    if (isOutOfView) {
      // Scroll the entire wrapped line into view, ensuring it is fully visible
      viewRef.dispatch({
        effects: EditorView.scrollIntoView(linePos, {
          y: "center",
          yMargin: 20, // Optional margin to ensure no clipping
        }),
      });
    }
  };

  useEffect(() => {
    if (currentExplanation?.highlightLines?.length && viewRef) {
      ensureLineCentered(currentExplanation.highlightLines[0]);
    }
  }, [currentHighlightIndex, viewRef, currentExplanation]);

  const riveAnaimtion = (
    <Center>
      <RiveComponent
        style={{
          width: riveAnimationWidth ?? "100%",
          height: riveAnimationHeight ?? isMobile ? "15rem" : "20rem",
        }}
      />
    </Center>
  );

  return (
    <Box
      maxWidth={{ base: "100%", md: "70rem" }}
      ml={{ base: 0, md: riveSrc ? "-40%" : 0 }}
      my={"1.5rem"}
      px={"1rem"}
    >
      <Box
        display={{ base: "block", md: riveSrc ? "flex" : "block" }}
        width={{ base: "100%", md: riveSrc ? "max-content" : "100%" }}
        gap={{ base: 0, md: "2rem" }}
        alignItems={"center"}
      >
        <Box alignSelf={"flex-start"} flex={1}>
          {riveSrc &&
            (riveAnimationPosition === "above-editor" ||
              riveAnimationPosition === null) &&
            riveAnaimtion}
        </Box>
        <Box flex={1} maxW={{ base: "100%", md: "30rem" }}>
          <Box
            border={"1px solid"}
            borderColor={"neutral.900"}
            borderTopRadius={".5rem"}
            bg={"#1E1E1E"}
            p={".5rem"}
          >
            <HStack justifyContent={"space-between"}>
              <Box>{language}</Box>
              <Box>{!noDots && <EditorDots />}</Box>
            </HStack>
            <Box ref={editorWrapperRef}>
              <CodeMirror
                style={{
                  fontSize: isMobile ? ".9rem" : "1rem",
                  width: "100%",
                  maxHeight: noScroll ? "auto" : isMobile ? "10vh" : "15vh",
                  overflow: "auto",
                }}
                value={code}
                minHeight={height}
                readOnly={
                  mode === "reveal" || mode === "view" || mode === "view-run"
                }
                extensions={[
                  python(),
                  highlightPlugin,
                  EditorView.lineWrapping,
                  ...(mode === "reveal" ||
                  mode === "view" ||
                  mode === "view-run"
                    ? [
                        EditorState.readOnly.of(true),
                        EditorView.editable.of(false),
                        EditorView.theme({
                          ".cm-activeLine": {
                            backgroundColor: "none",
                          },
                          ".cm-cursor": {
                            display: "none",
                          },
                        }),
                      ]
                    : []),
                ]}
                onChange={onChange}
                onCreateEditor={(view) => setViewRef(view)}
                theme={vscodeDark}
                basicSetup={{
                  lineNumbers: noLineNumbers ? false : true,
                  highlightActiveLineGutter:
                    mode === "reveal" || mode === "view" || mode === "view-run"
                      ? false
                      : true,
                  foldGutter: false,
                  dropCursor: false,
                  allowMultipleSelections: false,
                  indentOnInput: false,
                  bracketMatching: false,
                  closeBrackets: false,
                  autocompletion: false,
                  rectangularSelection: false,
                  crosshairCursor: false,
                  lintKeymap: false,
                  highlightActiveLine:
                    mode === "reveal" || mode === "view" || mode === "view-run"
                      ? false
                      : true,
                }}
              />
            </Box>
          </Box>

          <Box
            border={"1px solid"}
            borderColor={"neutral.800"}
            borderBottomRadius={".5rem"}
            borderTopColor={"transparent"}
          >
            {(mode === "try" ||
              mode === "reveal-try" ||
              mode === "reveal" ||
              mode === "view-run") && (
              <Box
                p={"1rem"}
                pt={"0.5rem"}
                borderBottom={"1px solid "}
                border={hightlight !== "neutral" ? "1px solid" : "none"}
                borderColor={
                  hightlight === "neutral"
                    ? "neutral.800"
                    : hightlight === "success"
                    ? "green.500"
                    : "yellow.600"
                }
              >
                <Text mb={".5rem"} color={"whiteAlpha.600"} fontSize={"sm"}>
                  output:{" "}
                  {mode === "reveal" || mode === "reveal-try" ? (
                    <Box as="span">
                      {codeExecute.isPending && <Spinner size={"sm"} />}
                    </Box>
                  ) : (
                    <></>
                  )}
                </Text>
                <Box
                  as="pre"
                  sx={{
                    whiteSpace: "pre-wrap",
                    fontsize: { base: "1rem", lg: "md" },
                  }}
                >
                  {output}
                </Box>
              </Box>
            )}

            {(mode === "try" ||
              mode === "reveal-try" ||
              mode === "view-run") && (
              <EditorControls
                code={code}
                language={language}
                crowded={crowded}
                noReset={noReset}
                isRunning={codeExecute.isPending}
                RunOnClick={runCode}
                ResetOnClick={() => {
                  setCode(initial_code);
                  setOutput("");
                  setHightlightColor(() => hightlight);
                }}
                isResetDisabled={false}
              />
            )}
          </Box>

          {riveSrc && riveAnimationPosition === "below-editor" && riveAnaimtion}

          {showExplanation && (
            <EditorExplanation
              explanation={currentExplanation}
              currentIdx={currentHighlightIndex}
              explanationsCount={explanation?.length}
              onPrevClick={handlePrevious}
              onPrevIsDisabled={!hasHighlights || currentHighlightIndex === 0}
              onNextClick={handleNext}
              onNextIsDisabled={
                !hasHighlights ||
                currentHighlightIndex === explanation!.length - 1
              }
            />
          )}
        </Box>
      </Box>

      {riveSrc &&
        riveAnimationPosition === "below-explanation" &&
        riveAnaimtion}
    </Box>
  );
}

export default CodeEditor;
